import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PackagePickupLog, PackagePickupInsert, PackagePickupUpdate } from "@/types/packagePickup";

export const usePackagePickupLogs = (startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ["package-pickup-logs", startDate, endDate],
    queryFn: async () => {
      console.log("[usePackagePickupLogs] Fetching logs", { startDate, endDate });
      
      let query = supabase
        .from("package_pickup_logs")
        .select("*")
        .order("pickup_date", { ascending: false });

      if (startDate) {
        query = query.gte("pickup_date", startDate);
      }
      if (endDate) {
        query = query.lte("pickup_date", endDate);
      }

      const { data, error } = await query;

      if (error) {
        console.error("[usePackagePickupLogs] Error fetching logs:", error);
        throw error;
      }

      console.log("[usePackagePickupLogs] Successfully fetched", data?.length || 0, "logs");
      return data as PackagePickupLog[];
    },
    staleTime: 30000, // 30 seconds
    retry: 2,
  });
};

export const useCreatePackagePickupLog = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: Omit<PackagePickupInsert, "registered_by_id" | "registered_by_name">) => {
      if (!user) throw new Error("Gebruiker niet gevonden");

      const insertData: PackagePickupInsert = {
        ...data,
        registered_by_id: user.id,
        registered_by_name: user.name,
      };

      const { data: result, error } = await supabase
        .from("package_pickup_logs")
        .insert(insertData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["package-pickup-logs"] });
      toast.success("Ophaaltijd succesvol geregistreerd");
    },
    onError: (error: any) => {
      console.error("Error creating pickup log:", error);
      
      // Show the specific error message for better debugging
      const errorMessage = error?.message || "Onbekende fout";
      console.log("Detailed error:", { error, message: errorMessage });
      
      if (error.code === "23505") {
        toast.error("Er is al een ophaaltijd geregistreerd voor deze datum");
      } else if (error.message?.includes("row-level security")) {
        toast.error(`RLS fout: Je hebt geen toestemming voor deze actie. Controleer je gebruikersrol. (${errorMessage})`);
      } else {
        toast.error(`Fout bij het registreren van de ophaaltijd: ${errorMessage}`);
      }
    },
  });
};

export const useUpdatePackagePickupLog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PackagePickupUpdate }) => {
      const { data: result, error } = await supabase
        .from("package_pickup_logs")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["package-pickup-logs"] });
      toast.success("Ophaaltijd succesvol bijgewerkt");
    },
    onError: (error: any) => {
      console.error("Error updating pickup log:", error);
      toast.error("Fout bij het bijwerken van de ophaaltijd");
    },
  });
};

export const useDeletePackagePickupLog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      console.log("Attempting to delete pickup log with ID:", id);
      
      const { data, error } = await supabase
        .from("package_pickup_logs")
        .delete()
        .eq("id", id)
        .select("id");

      if (error) {
        console.error("Supabase delete error:", error);
        throw error;
      }

      const deletedCount = data?.length ?? 0;
      console.log("Delete operation result:", { deletedCount, data, itemId: id });

      if (deletedCount === 0) {
        console.warn("No rows were deleted - checking if item exists and permissions...");
        throw new Error("Geen rij verwijderd. Controleer je permissies of probeer opnieuw.");
      }

      console.log("Successfully deleted pickup log:", id);

      return id;
    },
    onMutate: async (deletedId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["package-pickup-logs"] });

      // Snapshot the previous value
      const previousLogs = queryClient.getQueriesData({ queryKey: ["package-pickup-logs"] });

      // Optimistically update to the new value
      queryClient.setQueriesData({ queryKey: ["package-pickup-logs"] }, (old: any) => {
        if (!old) return old;
        return old.filter((log: any) => log.id !== deletedId);
      });

      // Return a context object with the snapshotted value
      return { previousLogs };
    },
    onError: (error: any, deletedId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousLogs) {
        context.previousLogs.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error("Error deleting pickup log:", error);
      toast.error("Fout bij het verwijderen van de ophaaltijd");
    },
    onSuccess: (deletedId) => {
      toast.success("Ophaaltijd succesvol verwijderd");
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === "package-pickup-logs"
      });
    },
  });
};